import {CatalogLogo} from '@rdcomponents/catalog-logo/catalog-logo';
import {Button, Flex, Tag} from 'antd';
import {SyntheticEvent, useEffect, useState} from 'react';

import {AntdErrorMessage} from '../../../../../../../component/antd-error-message/antd-error-message';
import {Text} from '../../../../../../../component/text/text';
import {spacer_m} from '../../../../../../../css/var-export.scss';
import {Spinner} from '../../../../../../../layout/spinner/spinner';
import {useDocumentationLinks} from '../../../../../../../provider/help-links/help-links-hook';
import {Locale} from '../../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../../../../../provider/locale/translation/type';
import {uploadCompanyVideoFile} from '../../../../../../../service/company/company-api';
import {useCompanyMediaFile} from '../../../../../../../service/company/company-hook';
import {
    CompanyImageKindEnum,
    CompanyUploadMediaFileErrorType,
    CompanyUploadVideoFileErrorType,
} from '../../../../../../../service/company/company-type';
import {CompanyGalleryImageType} from '../../../../../../../service/company-v2/types/company-gallery-type';
import {useLicensesDescription} from '../../../../../../../service/tariffs/tariffs-licenses-description';
import {classNames} from '../../../../../../../util/css';
import {useRefreshId} from '../../../../../../../util/hook';
import {ProvidersIdsEnum} from '../../../../../../../util/type';
import {getVideoDurationFromObjectUrl} from '../../../../../../../util/video';

import {CategoryFromGalleryFormItemEnum, categoryFromGalleryList} from './galery-form-item-const';
import {UploadCompanyFormDescription} from './gallery-form-item-helper';
import {GalleryMediaItem} from './gallery-image/gallery-image';
import {NewUploadButton} from './new-upload-button/new-upload-button';
import * as styles from './gallery-form-item.scss';

const MAX_VIDEO_DURATION_SEC = 60;

function canShowVideoAndCategories(
    licensesDescription:
        | Array<{
              licenseCode: string;
              providerCodes?: Array<{label: string; isActive: boolean}> | null;
          }>
        | undefined
): boolean {
    if (!licensesDescription?.length) {
        return true;
    }

    const catalogSyncLicense = licensesDescription.find(
        (license) => license.licenseCode === 'catalogs_synchronize_licence'
    );
    const yandexCatalog = catalogSyncLicense?.providerCodes?.find(
        (provider) => provider.label.toLowerCase() === 'yandex'
    );

    return Boolean(yandexCatalog?.isActive);
}

type PropsType = {
    value?: Array<CompanyGalleryImageType>;
    onChange?: (inputImageList: Array<CompanyGalleryImageType>) => void;
    errorMessage?: Array<string>;
    setGalleryError: (error: Array<string> | null) => void;
    catalogId?: number;
    maxSize?: LangKeyType | null;
    onImageUpload?: (temporaryIds: Array<string>) => void;
    companyId: number;
};

// eslint-disable-next-line complexity
export function GalleryFormItem(props: PropsType): JSX.Element {
    const {value: images = [], onChange: setImages, errorMessage, setGalleryError, catalogId, onImageUpload} = props;
    const [category, setCategory] = useState<CategoryFromGalleryFormItemEnum>(CategoryFromGalleryFormItemEnum.All);
    const {reset, uploadCompanyMediaFile} = useCompanyMediaFile();
    const {refreshId, refresh} = useRefreshId();
    const {getLocalizedString} = useLocale();
    const {data: licensesDescription} = useLicensesDescription();
    const {company} = useDocumentationLinks();
    const canShowVideoAndCategoriesFlag = canShowVideoAndCategories(licensesDescription);
    const [inProgress, setInProgress] = useState<boolean>(false);

    async function uploadFile(file: File) {
        const objectUrl = URL.createObjectURL(file);

        try {
            const {temp_id, warnings} = await uploadCompanyMediaFile({
                file,
                file_type: CompanyImageKindEnum.gallery,
                ...(catalogId ? {catalogId} : {}),
            });

            return {tempId: temp_id, objectUrl, warnings, size: file.size};
        } catch (error: unknown) {
            setInProgress(false);
            return {
                objectUrl,
                size: file.size,
                errorMessage:
                    error instanceof Error
                        ? ((JSON.parse(error.message) as CompanyUploadMediaFileErrorType) || {})?.file_s3
                        : [getLocalizedString('NOTIFICATION__TEXT__SERVER_ERROR')],
            };
        }
    }

    async function handleOnChange(event: SyntheticEvent<HTMLInputElement>) {
        setInProgress(true);

        const {files} = event.currentTarget;
        const fileList: Array<File> = [...(files || [])];
        const uploadedImages = await Promise.all(fileList.map(uploadFile));

        setImages?.([...images, ...uploadedImages]);
        refresh();
        reset();

        const temporaryIdsWithWarnings = uploadedImages
            .filter((image) => image.tempId && image.warnings?.length)
            .map((image) => image.tempId as string);

        if (temporaryIdsWithWarnings.length > 0) {
            onImageUpload?.(temporaryIdsWithWarnings);
        }

        setInProgress(false);
    }

    async function uploadVideo(file: File) {
        const objectUrl = URL.createObjectURL(file);

        try {
            const durationSec = await getVideoDurationFromObjectUrl(objectUrl);

            if (durationSec > MAX_VIDEO_DURATION_SEC) {
                return {
                    objectUrl,
                    size: file.size,
                    errorMessage: [getLocalizedString('COMPANY_FORM__GALLERY__ERROR__VIDEO_DURATION')],
                };
            }
        } catch {
            return {
                objectUrl,
                size: file.size,
                errorMessage: [getLocalizedString('COMPANY_FORM__GALLERY__ERROR__VIDEO_DURATION__INVALID')],
            };
        }

        try {
            const {tempId, thumbnail, video} = await uploadCompanyVideoFile(file);

            URL.revokeObjectURL(objectUrl);

            return {tempId, size: file.size, thumbnail, video};
        } catch (error: unknown) {
            if (error instanceof Error && error.message && error.message.includes('413 Request Entity Too Large')) {
                setGalleryError([getLocalizedString('NOTIFICATION__TEXT__SERVER_ERROR')]);
            }

            setInProgress(false);
            return {
                objectUrl,
                size: file.size,
                errorMessage:
                    error instanceof Error
                        ? ((JSON.parse(error.message) as CompanyUploadVideoFileErrorType) || {})?.video
                        : [getLocalizedString('NOTIFICATION__TEXT__SERVER_ERROR')],
            };
        }
    }

    async function handleOnChangeVideo(event: SyntheticEvent<HTMLInputElement>) {
        setInProgress(true);

        const {files} = event.currentTarget;
        const fileList: Array<File> = [...(files || [])];
        const uploadedVideos = await Promise.all(fileList.map(uploadVideo));

        setImages?.([...images, ...uploadedVideos]);
        refresh();
        reset();

        setInProgress(false);
    }

    useEffect(() => {
        setGalleryError(
            images.some(({errorMessage: _errorMessage}) => _errorMessage)
                ? [getLocalizedString('COMPANY_FORM__GALLERY__ERROR')]
                : null
        );
    }, [getLocalizedString, images, setGalleryError]);

    const filteredImages = images.filter((image) => {
        if (category === CategoryFromGalleryFormItemEnum.All) {
            return true;
        }

        if (category === CategoryFromGalleryFormItemEnum.Video) {
            return 'video' in image || 'thumbnail' in image;
        }

        return true;
    });

    return (
        <Flex gap={spacer_m} vertical>
            <Flex gap={spacer_m}>
                {canShowVideoAndCategoriesFlag &&
                    categoryFromGalleryList.map((item) =>
                        !catalogId ? (
                            <Button
                                className={classNames(styles.GalleryFormItem_categoryButton, {
                                    [styles.GalleryFormItem_categoryButton__active]: item.key === category,
                                })}
                                key={item.key}
                                onClick={() => setCategory(item.key)}
                                type="text"
                            >
                                <Locale stringKey={item.label} />
                            </Button>
                        ) : (
                            <span key={item.key} />
                        )
                    )}
            </Flex>
            {!catalogId && canShowVideoAndCategoriesFlag && (
                <Text
                    secondary
                    stringKey="COMPANY_FORM__UPLOAD_VIDEO_HELP"
                    valueMap={{
                        catalog: (
                            <Tag
                                className={styles.GalleryFormItem_catalogTag}
                                icon={
                                    <CatalogLogo
                                        catalogId={ProvidersIdsEnum.yandex}
                                        className={styles.GalleryFormItem_catalogIcon}
                                    />
                                }
                            >
                                <Text
                                    small
                                    stringKey="LOCAL_PACK__SEARCH_PHRASES_STATISTICS__COLUMN__TOP_FIVE__YANDEX__MAPS"
                                />
                            </Tag>
                        ),
                        link: company.requirementsForPhotosAndVideos ? (
                            <a href={company.requirementsForPhotosAndVideos}>
                                <Locale stringKey="COMPANY_FORM__UPLOAD_VIDEO_HELP__LINK" />
                            </a>
                        ) : null,
                    }}
                />
            )}
            <div className={styles.input_image_list}>
                {inProgress && (
                    <div className={styles.spinner__container}>
                        <Spinner />
                    </div>
                )}

                <Flex gap={spacer_m} wrap="wrap">
                    {category === CategoryFromGalleryFormItemEnum.All && (
                        <div className={classNames(styles.upload_place)}>
                            <input
                                accept="image/jpeg, image/jpg, image/png, image/gif"
                                className={styles.input_file}
                                key={refreshId + '-input'}
                                multiple
                                onChange={handleOnChange}
                                type="file"
                            />
                            <NewUploadButton title="COMPANY_FORM__UPLOAD_PHOTO_BUTTON__TITLE">
                                <UploadCompanyFormDescription catalogId={catalogId as ProvidersIdsEnum} type="photo" />
                            </NewUploadButton>
                        </div>
                    )}
                    {!catalogId &&
                        canShowVideoAndCategoriesFlag &&
                        (category === CategoryFromGalleryFormItemEnum.All ||
                            category === CategoryFromGalleryFormItemEnum.Video) && (
                            <div className={classNames(styles.upload_place)}>
                                <input
                                    accept="video/mp4, video/webm, video/ogg, video/quicktime, .mov"
                                    className={styles.input_file}
                                    key={refreshId + '-input-video'}
                                    multiple
                                    onChange={handleOnChangeVideo}
                                    type="file"
                                />
                                <NewUploadButton title="COMPANY_FORM__UPLOAD_VIDEO_BUTTON__TITLE">
                                    <UploadCompanyFormDescription
                                        catalogId={catalogId as ProvidersIdsEnum}
                                        type="video"
                                    />
                                </NewUploadButton>
                            </div>
                        )}

                    {filteredImages.map((inputImage: CompanyGalleryImageType): JSX.Element => {
                        const {tempId, errorMessage: _errorMessage, warnings} = inputImage;
                        const hash = 'hash' in inputImage ? inputImage.hash : undefined; // eslint-disable-line no-undefined
                        const url = 'url' in inputImage ? inputImage.url : undefined; // eslint-disable-line no-undefined
                        const id = 'id' in inputImage ? inputImage.id : undefined; // eslint-disable-line no-undefined

                        return (
                            <GalleryMediaItem
                                className={classNames(_errorMessage ? styles.image_error : '')}
                                errorMessage={_errorMessage}
                                inputImage={inputImage}
                                key={`${tempId || hash || id}-${url || ''}`}
                                modifications={warnings}
                                onRemove={() => setImages?.(images.filter((item) => item !== inputImage))}
                            />
                        );
                    })}
                </Flex>
            </div>

            {errorMessage && <AntdErrorMessage className={styles.error}>{errorMessage}</AntdErrorMessage>}
        </Flex>
    );
}
